import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../api/client';
import StatusBadge from '../components/shared/StatusBadge';
import ReplyIssuesAlert from '../components/tickets/ReplyIssuesAlert';
import { cn } from '../utils/cn';
import { tagLabel } from '../utils/tagLabel';
import {
  ArrowLeft, Send, Zap, AlertTriangle, Key, Tag, Brain,
  User, Bot, Clock, RefreshCw, Archive, Star,
} from 'lucide-react';

const TAG_COLORS = {
  from_imap: 'bg-blue-100 text-blue-700',
  autopilot_sent: 'bg-purple-100 text-purple-700',
  replacement_key_sent: 'bg-green-100 text-green-700',
  escalated: 'bg-red-100 text-red-700',
  urgent: 'bg-red-100 text-red-700',
  vip: 'bg-yellow-100 text-yellow-700',
};

export default function TicketDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [processingAi, setProcessingAi] = useState(false);
  const [replyIssues, setReplyIssues] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');

  const fetchTicket = useCallback(async () => {
    try {
      const [ticketRes, productsRes] = await Promise.all([
        api.get(`/tickets/${id}`),
        api.get('/products'),
      ]);
      setTicket(ticketRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      console.error('Failed to fetch ticket:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchTicket(); }, [fetchTicket]);

  const handleSendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/send-email/${id}`, { content: reply });
      setReply('');
      setReplyIssues([]);
      fetchTicket();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const handleAiProcess = async () => {
    setProcessingAi(true);
    try {
      const response = await api.post('/ai/process-ticket', { ticket_id: id });
      setReplyIssues(response.data.reply_issues || []);
      alert(`Intent: ${response.data.intent}\nConfidence: ${(response.data.confidence * 100).toFixed(0)}%`);
      fetchTicket();
    } catch (error) {
      console.error('AI processing failed:', error);
    } finally {
      setProcessingAi(false);
    }
  };

  const handleAiGenerate = async () => {
    setProcessingAi(true);
    try {
      const response = await api.post('/ai/generate-response', { ticket_id: id });
      setReply(response.data.response);
      setReplyIssues(response.data.reply_issues || []);
    } catch (error) {
      console.error('AI generation failed:', error);
    } finally {
      setProcessingAi(false);
    }
  };

  const handleAutopilot = async () => {
    if (!window.confirm(t('tickets.autopilot_confirm'))) return;
    setProcessingAi(true);
    try {
      const response = await api.post(`/autopilot/process/${id}`);
      alert(`Autopilot result:\n\nIntent: ${response.data.intent}\nConfidence: ${(response.data.confidence * 100).toFixed(0)}%\nAuto-sent: ${response.data.auto_sent ? 'Yes' : 'No'}\nReason: ${response.data.reason}`);
      fetchTicket();
    } catch (error) {
      console.error('Autopilot failed:', error);
    } finally {
      setProcessingAi(false);
    }
  };

  const handleStatusChange = async (status) => {
    try {
      await api.put(`/tickets/${id}`, { status });
      fetchTicket();
    } catch (error) {
      console.error('Status update failed:', error);
    }
  };

  const handleEscalate = async () => {
    if (!window.confirm(t('tickets.escalate_confirm'))) return;
    try {
      await api.post(`/tickets/${id}/escalate`);
      fetchTicket();
    } catch (error) {
      console.error('Escalation failed:', error);
    }
  };

  const handleArchiveToggle = async () => {
    try {
      await api.post(`/tickets/${id}/${ticket.archived_at ? 'unarchive' : 'archive'}`);
      fetchTicket();
    } catch (error) {
      console.error('Archive toggle failed:', error);
    }
  };

  const handleSendReview = async () => {
    if (!window.confirm(t('tickets.send_review_confirm'))) return;
    try {
      const response = await api.post(`/tickets/${id}/send-review`);
      alert(response.data.message || t('tickets.review_sent'));
      fetchTicket();
    } catch (error) {
      console.error('Review mail failed:', error);
      alert(t('tickets.review_failed'));
    }
  };

  const handleReplaceKey = async () => {
    if (!selectedProduct) { alert(t('tickets.select_product_first')); return; }
    if (!window.confirm(t('tickets.send_replacement_confirm'))) return;
    try {
      const response = await api.post(`/tickets/${id}/auto-replace-key`, { product_id: selectedProduct });
      alert(response.data.message);
      fetchTicket();
    } catch (error) {
      alert(error.response?.data?.error || 'Key replacement failed');
    }
  };

  const handleAddTag = async () => {
    if (!newTag.trim()) return;
    const tags = [...(ticket.tags || []), newTag.trim().toLowerCase().replace(/\s+/g, '_')];
    try {
      await api.put(`/tickets/${id}`, { tags: [...new Set(tags)] });
      setNewTag('');
      fetchTicket();
    } catch (error) {
      console.error('Tag add failed:', error);
    }
  };

  const handleRemoveTag = async (tag) => {
    const tags = (ticket.tags || []).filter((t) => t !== tag);
    try {
      await api.put(`/tickets/${id}`, { tags });
      fetchTicket();
    } catch (error) {
      console.error('Tag remove failed:', error);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div>;
  if (!ticket) return <div className="text-center py-12 text-gray-500">{t('tickets.ticket_not_found')}</div>;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/tickets')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <ArrowLeft className="h-5 w-5 text-gray-500" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-gray-900 truncate">{ticket.subject}</h1>
          <p className="text-sm text-gray-500">{ticket.customer_email} &middot; {new Date(ticket.created_at).toLocaleString()}</p>
          {ticket.reply_to_email && ticket.reply_to_email !== ticket.customer_email && (
            <p className="text-xs text-gray-400">{t('tickets.reply_goes_to', { email: ticket.reply_to_email })}</p>
          )}
        </div>
        <StatusBadge status={ticket.status} className="text-sm" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Conversation */}
        <div className="lg:col-span-2 space-y-4">
          {/* Messages */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900">{t('tickets.conversation')}</h2>
            </div>
            <div className="divide-y divide-gray-100 max-h-[500px] overflow-y-auto">
              {ticket.messages?.map((msg) => (
                <div key={msg.id} className={cn('px-6 py-4', msg.sender === 'support' ? 'bg-blue-50/50' : '')}>
                  <div className="flex items-center gap-2 mb-2">
                    {msg.sender === 'customer' ? (
                      <User className="h-4 w-4 text-gray-400" />
                    ) : msg.is_ai_generated ? (
                      <Bot className="h-4 w-4 text-purple-500" />
                    ) : (
                      <User className="h-4 w-4 text-blue-500" />
                    )}
                    <span className="text-sm font-medium text-gray-700">
                      {msg.sender === 'customer' ? t('tickets.customer') : t('tickets.support')}
                    </span>
                    {msg.is_ai_generated && <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">{t('tickets.ai')}</span>}
                    <span className="text-xs text-gray-400 ml-auto">
                      <Clock className="inline h-3 w-3 mr-1" />
                      {new Date(msg.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{msg.content}</p>
                </div>
              ))}
            </div>
          </div>

          {/* AI Suggestion */}
          {ticket.ai_suggestion && (
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Brain className="h-4 w-4 text-purple-600" />
                <span className="text-sm font-semibold text-purple-700">{t('tickets.ai_suggestion')}</span>
              </div>
              <p className="text-sm text-purple-800 whitespace-pre-wrap">{ticket.ai_suggestion}</p>
              <button
                onClick={() => setReply(ticket.ai_suggestion)}
                className="mt-2 text-xs text-purple-600 hover:text-purple-800 font-medium"
              >
                {t('tickets.use_as_reply')}
              </button>
            </div>
          )}

          {/* Reply box */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <ReplyIssuesAlert issues={replyIssues} />
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder={t('tickets.reply_placeholder')}
              rows={4}
              className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm mb-3"
            />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  onClick={handleAiGenerate}
                  disabled={processingAi}
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-purple-300 text-purple-700 rounded-lg text-sm hover:bg-purple-50 transition-colors disabled:opacity-50"
                >
                  <Brain className="h-4 w-4" />
                  {processingAi ? '...' : t('tickets.ai_generate')}
                </button>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSendReply}
                  disabled={!reply.trim() || sending}
                  className="flex items-center gap-1.5 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  <Send className="h-4 w-4" />
                  {sending ? t('tickets.sending') : t('tickets.send_reply')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Actions */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-3">
            <h3 className="font-semibold text-gray-900 text-sm">{t('common.actions')}</h3>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">{t('filters.status')}</label>
              <select
                value={ticket.status}
                onChange={(e) => handleStatusChange(e.target.value)}
                className="block w-full rounded-lg border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"
              >
                <option value="new">{t('status.new')}</option>
                <option value="in_progress">{t('status.in_progress')}</option>
                <option value="info_requested">{t('status.info_requested')}</option>
                <option value="waiting_customer">{t('status.waiting_customer')}</option>
                <option value="resolved">{t('status.resolved')}</option>
                <option value="escalated">{t('status.escalated')}</option>
              </select>
            </div>

            <button onClick={handleAiProcess} disabled={processingAi} className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-purple-300 text-purple-700 rounded-lg text-sm hover:bg-purple-50 transition-colors disabled:opacity-50">
              <Brain className="h-4 w-4" /> {processingAi ? '...' : t('tickets.ai_classify')}
            </button>
            <button onClick={handleAutopilot} disabled={processingAi} className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-purple-300 text-purple-700 rounded-lg text-sm hover:bg-purple-50 transition-colors disabled:opacity-50">
              <Zap className="h-4 w-4" /> {t('tickets.run_autopilot')}
            </button>
            <button onClick={handleEscalate} className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-red-300 text-red-700 rounded-lg text-sm hover:bg-red-50 transition-colors">
              <AlertTriangle className="h-4 w-4" /> {t('tickets.escalate')}
            </button>
            <button onClick={handleArchiveToggle} className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 transition-colors">
              <Archive className="h-4 w-4" /> {ticket.archived_at ? t('tickets.unarchive') : t('tickets.archive_action')}
            </button>
            {ticket.status === 'resolved' && (
              <button onClick={handleSendReview} className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-amber-300 text-amber-700 rounded-lg text-sm hover:bg-amber-50 transition-colors">
                <Star className="h-4 w-4" /> {ticket.review_mail_sent_at ? t('tickets.review_resend') : t('tickets.send_review')}
              </button>
            )}
          </div>

          {/* Key Replacement */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-3">
            <h3 className="font-semibold text-gray-900 text-sm flex items-center gap-2">
              <Key className="h-4 w-4" /> {t('tickets.key_replacement')}
            </h3>
            {ticket.replacement_key_sent && (
              <p className="text-xs text-green-600 font-medium">{t('tickets.replacement_already_sent')}</p>
            )}
            <select
              value={selectedProduct}
              onChange={(e) => setSelectedProduct(e.target.value)}
              className="block w-full rounded-lg border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">{t('tickets.select_product')}</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.name} ({t('tickets.available_count', { count: p.stock })})</option>)}
            </select>
            <button onClick={handleReplaceKey} className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors">
              <RefreshCw className="h-4 w-4" /> {t('tickets.send_replacement_key')}
            </button>
          </div>

          {/* Tags */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-3">
            <h3 className="font-semibold text-gray-900 text-sm flex items-center gap-2">
              <Tag className="h-4 w-4" /> {t('tickets.tags')}
            </h3>
            <div className="flex flex-wrap gap-1.5">
              {ticket.tags?.map((tag) => (
                <span key={tag} className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium', TAG_COLORS[tag] || 'bg-gray-100 text-gray-600')}>
                  {tagLabel(tag, t)}
                  <button onClick={() => handleRemoveTag(tag)} className="hover:text-red-600">×</button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input type="text" value={newTag} onChange={(e) => setNewTag(e.target.value)} placeholder={t('tickets.add_tag')} className="flex-1 rounded-lg border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500" onKeyDown={(e) => e.key === 'Enter' && handleAddTag()} />
              <button onClick={handleAddTag} className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition-colors">{t('tickets.add')}</button>
            </div>
          </div>

          {/* Info */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-2">
            <h3 className="font-semibold text-gray-900 text-sm">{t('common.details')}</h3>
            <div className="text-sm space-y-1.5">
              <p><span className="text-gray-500">{t('tickets.intent')}:</span> <span className="font-medium">{ticket.intent ? t(`intent.${ticket.intent}`, { defaultValue: ticket.intent.replace(/_/g, ' ') }) : t('intent.not_classified')}</span></p>
              <p><span className="text-gray-500">{t('tickets.language')}:</span> <span className="font-medium">{ticket.language?.toUpperCase()}</span></p>
              <p><span className="text-gray-500">{t('tickets.shop')}:</span> <span className="font-medium">{ticket.shop?.name}</span></p>
              {ticket.detected_error_code && (
                <p><span className="text-gray-500">{t('tickets.error_code')}:</span> <span className="font-mono font-medium text-red-600">{ticket.detected_error_code}</span></p>
              )}
              {ticket.email_account && (
                <p><span className="text-gray-500">{t('tickets.via')}:</span> <span className="font-medium">{ticket.email_account.email}</span></p>
              )}
              {ticket.status === 'escalated' && ticket.email_account?.escalation_email && ticket.email_account?.escalation_smtp_host && (
                <p><span className="text-gray-500">{t('tickets.reply_from')}:</span> <span className="font-medium">{ticket.email_account.escalation_email}</span></p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
